import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  Users,
  Phone,
  Mail,
  Building,
  Shield,
  CheckCircle,
  Clock,
  UserX,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/shared/utils';
import {
  CommunityResident,
  ContactGroup,
  ResidentSearchFilters,
  SYSTEM_CONTACT_GROUPS,
} from '@/types/contacts';

interface ContactDirectoryProps {
  className?: string;
  selectionMode?: 'single' | 'multiple' | 'none';
  selectedContacts?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  onContactSelect?: (contact: CommunityResident) => void;
  showFilters?: boolean;
  showGroups?: boolean;
  maxSelections?: number;
}

// Mock data for demonstration
const mockResidents: CommunityResident[] = [
  {
    id: '1',
    tenantId: 'community-1',
    firstName: 'Sarah',
    lastName: 'Johnson',
    email: 'sarah.johnson@email.com',
    phone: '+1 (555) 123-4567',
    unitNumber: 'A-301',
    building: 'Building A',
    avatarUrl: '/placeholder.svg',
    role: 'board_member',
    status: 'active',
    isOnline: true,
    joinedAt: '2023-01-15T00:00:00Z',
    verificationStatus: 'verified',
    preferences: {
      allowDirectMessages: true,
      allowGroupInvitations: true,
      showOnlineStatus: true,
      allowContactImport: false,
    },
  },
  {
    id: '2',
    tenantId: 'community-1',
    firstName: 'Michael',
    lastName: 'Chen',
    email: 'michael.chen@email.com',
    phone: '+1 (555) 234-5678',
    unitNumber: 'B-205',
    building: 'Building B',
    avatarUrl: '/placeholder.svg',
    role: 'resident',
    status: 'active',
    isOnline: false,
    lastSeen: '2024-01-24T14:30:00Z',
    joinedAt: '2023-03-20T00:00:00Z',
    verificationStatus: 'verified',
    preferences: {
      allowDirectMessages: true,
      allowGroupInvitations: true,
      showOnlineStatus: false,
      allowContactImport: true,
    },
  },
  {
    id: '3',
    tenantId: 'community-1',
    firstName: 'Emma',
    lastName: 'Wilson',
    email: 'emma.wilson@email.com',
    phone: '+1 (555) 345-6789',
    unitNumber: 'C-102',
    building: 'Building C',
    avatarUrl: '/placeholder.svg',
    role: 'resident',
    status: 'active',
    isOnline: true,
    joinedAt: '2023-07-10T00:00:00Z',
    verificationStatus: 'verified',
    preferences: {
      allowDirectMessages: true,
      allowGroupInvitations: false,
      showOnlineStatus: true,
      allowContactImport: false,
    },
  },
  {
    id: '4',
    tenantId: 'community-1',
    firstName: 'David',
    lastName: 'Brown',
    email: 'david.brown@email.com',
    phone: '+1 (555) 456-7890',
    unitNumber: 'D-205',
    building: 'Building D',
    avatarUrl: '/placeholder.svg',
    role: 'maintenance',
    status: 'active',
    isOnline: false,
    lastSeen: '2024-01-24T09:15:00Z',
    joinedAt: '2023-02-01T00:00:00Z',
    verificationStatus: 'verified',
    preferences: {
      allowDirectMessages: true,
      allowGroupInvitations: true,
      showOnlineStatus: true,
      allowContactImport: false,
    },
  },
  {
    id: '5',
    tenantId: 'community-1',
    firstName: 'Lisa',
    lastName: 'Garcia',
    email: 'lisa.garcia@email.com',
    phone: '+1 (555) 567-8901',
    unitNumber: 'E-101',
    building: 'Building E',
    avatarUrl: '/placeholder.svg',
    role: 'property_manager',
    status: 'active',
    isOnline: true,
    joinedAt: '2022-12-01T00:00:00Z',
    verificationStatus: 'verified',
    preferences: {
      allowDirectMessages: true,
      allowGroupInvitations: true,
      showOnlineStatus: true,
      allowContactImport: true,
    },
  },
];

const mockContactGroups: ContactGroup[] = [
  {
    id: 'group-1',
    name: 'Board Members',
    description: 'Community board members and leadership',
    type: 'role_based',
    members: mockResidents.filter((r) => r.role === 'board_member'),
    createdBy: '1',
    isPrivate: false,
    canAddMembers: false,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z',
  },
  {
    id: 'group-2',
    name: 'Building A Residents',
    description: 'All residents in Building A',
    type: 'system_generated',
    members: mockResidents.filter((r) => r.building === 'Building A'),
    createdBy: 'system',
    isPrivate: false,
    canAddMembers: false,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z',
  },
  {
    id: 'group-3',
    name: 'Property Management',
    description: 'Property management team and maintenance staff',
    type: 'role_based',
    members: mockResidents.filter((r) => ['property_manager', 'maintenance'].includes(r.role)),
    createdBy: 'system',
    isPrivate: false,
    canAddMembers: false,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z',
  },
];

export function ContactDirectory({
  className,
  selectionMode = 'none',
  selectedContacts = [],
  onSelectionChange,
  onContactSelect,
  showFilters = true,
  showGroups = true,
  maxSelections,
}: ContactDirectoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ResidentSearchFilters>({});
  const [selectedTab, setSelectedTab] = useState<'residents' | 'groups'>('residents');
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  const handleContactSelection = (contactId: string, contact: CommunityResident) => {
    if (selectionMode === 'none') {
      onContactSelect?.(contact);
      return;
    }

    if (selectionMode === 'single') {
      onSelectionChange?.([contactId]);
      onContactSelect?.(contact);
      return;
    }

    // Multiple selection
    const isSelected = selectedContacts.includes(contactId);
    let newSelection: string[];

    if (isSelected) {
      newSelection = selectedContacts.filter((id) => id !== contactId);
    } else {
      if (maxSelections && selectedContacts.length >= maxSelections) {
        return; // Don't allow more selections than max
      }
      newSelection = [...selectedContacts, contactId];
    }

    onSelectionChange?.(newSelection);
  };

  const filteredResidents = mockResidents.filter((resident) => {
    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const fullName = `${resident.firstName} ${resident.lastName}`.toLowerCase();
      const unitMatch = resident.unitNumber.toLowerCase().includes(query);
      const emailMatch = resident.email.toLowerCase().includes(query);

      if (!fullName.includes(query) && !unitMatch && !emailMatch) {
        return false;
      }
    }

    // Role filter
    if (filters.role && resident.role !== filters.role) return false;

    // Status filter
    if (filters.status && resident.status !== filters.status) return false;

    // Verification filter
    if (filters.verificationStatus && resident.verificationStatus !== filters.verificationStatus)
      return false;

    // Building filter
    if (filters.building && resident.building !== filters.building) return false;

    // Online status filter
    if (filters.isOnline !== undefined && resident.isOnline !== filters.isOnline) return false;

    // Can message filter
    if (filters.canMessage && !resident.preferences.allowDirectMessages) return false;

    return true;
  });

  const filteredGroups = mockContactGroups.filter((group) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const nameMatch = group.name.toLowerCase().includes(query);
      const descMatch = group.description?.toLowerCase().includes(query);

      if (!nameMatch && !descMatch) {
        return false;
      }
    }

    return true;
  });

  const formatLastSeen = (lastSeen: string) => {
    const date = new Date(lastSeen);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Recently';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  const getRoleIcon = (role: CommunityResident['role']) => {
    switch (role) {
      case 'board_member':
        return <Shield className="w-3 h-3" />;
      case 'property_manager':
        return <Building className="w-3 h-3" />;
      case 'maintenance':
        return <Building className="w-3 h-3" />;
      default:
        return <Users className="w-3 h-3" />;
    }
  };

  const getRoleBadgeVariant = (role: CommunityResident['role']) => {
    switch (role) {
      case 'board_member':
        return 'default';
      case 'property_manager':
        return 'secondary';
      case 'maintenance':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getVerificationIcon = (status: CommunityResident['verificationStatus']) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="w-3 h-3 text-green-600" />;
      case 'pending':
        return <Clock className="w-3 h-3 text-yellow-600" />;
      case 'rejected':
        return <UserX className="w-3 h-3 text-red-600" />;
    }
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search and Filter Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search residents by name, unit, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          {showFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={cn(showFilterPanel && 'bg-muted')}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          )}
        </div>

        {/* Filter Panel */}
        {showFilterPanel && showFilters && (
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Role</label>
                  <Select
                    value={filters.role || ''}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        role: (value as CommunityResident['role']) || undefined,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All roles</SelectItem>
                      <SelectItem value="resident">Resident</SelectItem>
                      <SelectItem value="board_member">Board Member</SelectItem>
                      <SelectItem value="property_manager">Property Manager</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Status</label>
                  <Select
                    value={filters.status || ''}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        status: (value as CommunityResident['status']) || undefined,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="moved_out">Moved Out</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Building</label>
                  <Select
                    value={filters.building || ''}
                    onValueChange={(value) =>
                      setFilters((prev) => ({ ...prev, building: value || undefined }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All buildings" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All buildings</SelectItem>
                      <SelectItem value="Building A">Building A</SelectItem>
                      <SelectItem value="Building B">Building B</SelectItem>
                      <SelectItem value="Building C">Building C</SelectItem>
                      <SelectItem value="Building D">Building D</SelectItem>
                      <SelectItem value="Building E">Building E</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Options</label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="online-only"
                        checked={filters.isOnline === true}
                        onCheckedChange={(checked) =>
                          setFilters((prev) => ({ ...prev, isOnline: checked ? true : undefined }))
                        }
                      />
                      <label htmlFor="online-only" className="text-sm">
                        Online only
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="can-message"
                        checked={filters.canMessage === true}
                        onCheckedChange={(checked) =>
                          setFilters((prev) => ({
                            ...prev,
                            canMessage: checked ? true : undefined,
                          }))
                        }
                      />
                      <label htmlFor="can-message" className="text-sm">
                        Can message
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setFilters({})}>
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      {showGroups ? (
        <Tabs
          value={selectedTab}
          onValueChange={(value) => setSelectedTab(value as 'residents' | 'groups')}
        >
          <TabsList>
            <TabsTrigger value="residents">Residents ({filteredResidents.length})</TabsTrigger>
            <TabsTrigger value="groups">Groups ({filteredGroups.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="residents" className="mt-4">
            <ScrollArea className="h-[300px]">
              <div className="pr-4">
                <ContactList
                  residents={filteredResidents}
                  selectionMode={selectionMode}
                  selectedContacts={selectedContacts}
                  onContactSelection={handleContactSelection}
                  formatLastSeen={formatLastSeen}
                  getRoleIcon={getRoleIcon}
                  getRoleBadgeVariant={getRoleBadgeVariant}
                  getVerificationIcon={getVerificationIcon}
                />
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="groups" className="mt-4">
            <GroupList
              groups={filteredGroups}
              selectionMode={selectionMode}
              selectedContacts={selectedContacts}
              onGroupSelection={(groupId, group) => {
                // Handle group selection if needed
                console.log('Group selected:', groupId, group);
              }}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="mt-4">
          <ScrollArea className="h-[300px]">
            <div className="pr-4">
              <ContactList
                residents={filteredResidents}
                selectionMode={selectionMode}
                selectedContacts={selectedContacts}
                onContactSelection={handleContactSelection}
                formatLastSeen={formatLastSeen}
                getRoleIcon={getRoleIcon}
                getRoleBadgeVariant={getRoleBadgeVariant}
                getVerificationIcon={getVerificationIcon}
              />
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Selection Summary */}
      {selectionMode === 'multiple' && selectedContacts.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
                {maxSelections && ` (max ${maxSelections})`}
              </span>
              <Button variant="ghost" size="sm" onClick={() => onSelectionChange?.([])}>
                Clear Selection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface ContactListProps {
  residents: CommunityResident[];
  selectionMode: 'single' | 'multiple' | 'none';
  selectedContacts: string[];
  onContactSelection: (contactId: string, contact: CommunityResident) => void;
  formatLastSeen: (lastSeen: string) => string;
  getRoleIcon: (role: CommunityResident['role']) => React.ReactNode;
  getRoleBadgeVariant: (role: CommunityResident['role']) => 'default' | 'secondary' | 'outline';
  getVerificationIcon: (status: CommunityResident['verificationStatus']) => React.ReactNode;
}

function ContactList({
  residents,
  selectionMode,
  selectedContacts,
  onContactSelection,
  formatLastSeen,
  getRoleIcon,
  getRoleBadgeVariant,
  getVerificationIcon,
}: ContactListProps) {
  return (
    <div className="space-y-2">
      {residents.map((resident) => {
        const isSelected = selectedContacts.includes(resident.id);

        return (
          <Card
            key={resident.id}
            className={cn(
              'cursor-pointer transition-colors hover:bg-muted/50',
              isSelected && 'border-primary bg-primary/5',
              selectionMode !== 'none' && 'select-none',
            )}
            onClick={() => onContactSelection(resident.id, resident)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                {selectionMode === 'multiple' && (
                  <Checkbox
                    checked={isSelected}
                    onChange={() => {}} // Handled by card click
                  />
                )}

                <div className="relative">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={resident.avatarUrl} />
                    <AvatarFallback>
                      {resident.firstName[0]}
                      {resident.lastName[0]}
                    </AvatarFallback>
                  </Avatar>

                  {resident.preferences.showOnlineStatus && (
                    <div
                      className={cn(
                        'absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-background',
                        resident.isOnline ? 'bg-green-500' : 'bg-gray-400',
                      )}
                    />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium truncate">
                      {resident.firstName} {resident.lastName}
                    </h4>
                    {getVerificationIcon(resident.verificationStatus)}
                    <Badge variant={getRoleBadgeVariant(resident.role)} className="text-xs">
                      <span className="mr-1">{getRoleIcon(resident.role)}</span>
                      {resident.role.replace('_', ' ')}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Building className="w-3 h-3" />
                      {resident.unitNumber}
                    </div>
                    {resident.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {resident.phone}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {resident.email}
                    </div>
                  </div>

                  <div className="mt-1 text-xs text-muted-foreground">
                    {resident.isOnline
                      ? 'Online now'
                      : resident.lastSeen
                        ? `Last seen ${formatLastSeen(resident.lastSeen)}`
                        : 'Status unknown'}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!resident.preferences.allowDirectMessages && (
                    <Badge variant="outline" className="text-xs">
                      No DM
                    </Badge>
                  )}
                  {!resident.preferences.allowGroupInvitations && (
                    <Badge variant="outline" className="text-xs">
                      No Groups
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {residents.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No residents found</h3>
            <p className="text-muted-foreground">Try adjusting your search criteria or filters.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface GroupListProps {
  groups: ContactGroup[];
  selectionMode: 'single' | 'multiple' | 'none';
  selectedContacts: string[];
  onGroupSelection: (groupId: string, group: ContactGroup) => void;
}

function GroupList({ groups, selectionMode, selectedContacts, onGroupSelection }: GroupListProps) {
  return (
    <ScrollArea className="h-[400px]">
      <div className="space-y-2 pr-2">
        {groups.map((group) => (
          <Card
            key={group.id}
            className="cursor-pointer transition-colors hover:bg-muted/50"
            onClick={() => onGroupSelection(group.id, group)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarFallback>
                    <Users className="w-6 h-6" />
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium truncate">{group.name}</h4>
                    <Badge variant="outline" className="text-xs">
                      {group.type.replace('_', ' ')}
                    </Badge>
                    {group.isPrivate && (
                      <Badge variant="secondary" className="text-xs">
                        Private
                      </Badge>
                    )}
                  </div>

                  {group.description && (
                    <p className="text-sm text-muted-foreground mb-1 truncate">
                      {group.description}
                    </p>
                  )}

                  <div className="text-xs text-muted-foreground">
                    {group.members.length} member{group.members.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {groups.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No groups found</h3>
              <p className="text-muted-foreground">Try adjusting your search criteria.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </ScrollArea>
  );
}
